<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { reportCurrency } from 'dashboard/helper/currencyFormat';
import { KPI_CATEGORIES } from 'dashboard/routes/dashboard/settings/reports/helpers/reportColors';

const props = defineProps({
  label: { type: String, required: true },
  value: { type: [Number, String], default: 0 },
  format: { type: String, default: 'number' }, // number | currency | percent | time
  // Moeda ISO e locale opcionais — hardcode R$/pt-BR mentia p/ conta em
  // outra moeda (herdado do ReportKpiCard na consolidacao E2b).
  // vazio = usa a moeda da conta (reportCurrency); a prop existe para casos
  // com moeda propria, como custo de anuncio importado em outra moeda
  currency: { type: String, default: '' },
  locale: { type: String, default: 'pt-BR' },
  icon: { type: String, default: '' },
  trend: { type: Number, default: null },
  invertTrend: { type: Boolean, default: false },
  previousValue: { type: Number, default: null },
  showDelta: { type: Boolean, default: true },
  subtitle: { type: String, default: '' },
  tooltip: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  // Barra de cor no topo (assinatura BI Smart²): revenue | risk | time |
  // volume | highlight. Vazio = sem barra.
  category: { type: String, default: '' },
});

const { t } = useI18n();

const showTooltip = ref(false);

// Barra sempre presente (anatomia MetricCard do BI): sem categoria = azul info.
const categoryColor = computed(
  () => KPI_CATEGORIES[props.category] || '#6B8AAF'
);

// Count-up (IDV BI Smart²): anima o valor numerico no mount. Respeita
// prefers-reduced-motion e valores nao-numericos passam direto.
const displayValue = ref(0);
let rafId = null;

const reducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

const isAnimatable = computed(() => {
  if (props.value === null || props.value === undefined || props.value === '')
    return false;
  return !Number.isNaN(Number(props.value));
});

const animateTo = target => {
  if (rafId) cancelAnimationFrame(rafId);
  if (reducedMotion || !isAnimatable.value) {
    displayValue.value = target;
    return;
  }
  const from = Number(displayValue.value) || 0;
  const start = performance.now();
  const duration = 650;
  const step = now => {
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - (1 - progress) ** 3;
    displayValue.value = from + (target - from) * eased;
    if (progress < 1) rafId = requestAnimationFrame(step);
  };
  rafId = requestAnimationFrame(step);
};

onMounted(() => {
  if (isAnimatable.value) animateTo(Number(props.value));
});

watch(
  () => props.value,
  v => {
    if (isAnimatable.value) animateTo(Number(v));
  }
);

onBeforeUnmount(() => rafId && cancelAnimationFrame(rafId));

const formattedValue = computed(() => {
  // Sem dado != zero: null/undefined/'' exibe '—' (0% mentia).
  if (props.value === null || props.value === undefined || props.value === '')
    return '—';
  // String nao-numerica (ex.: valor pre-formatado "3h 20m") passa direto —
  // Number(str)||0 exibia "0" nos cards de tempo do drill de agentes.
  const num = Number(props.value);
  if (typeof props.value === 'string' && Number.isNaN(num)) return props.value;
  // Valor animado (count-up); inteiro para formato number
  const raw = isAnimatable.value ? Number(displayValue.value) : num;
  const v = (props.format === 'number' ? Math.round(raw) : raw) || 0;
  switch (props.format) {
    case 'currency':
      return new Intl.NumberFormat(props.locale, {
        style: 'currency',
        currency: props.currency || reportCurrency.value,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);
    case 'percent':
      return `${v.toFixed(1)}%`;
    case 'time': {
      // Unidade padrao do app: SEGUNDOS (igual formatTime/backend).
      const hours = v / 3600;
      if (hours <= 0) return '-';
      if (hours >= 24)
        return `${Math.floor(hours / 24)}d ${Math.round(hours % 24)}h`;
      if (hours >= 1)
        return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m`;
      return `${Math.round(hours * 60)}m`;
    }
    default:
      return v.toLocaleString(props.locale);
  }
});

const trendIsPositive = computed(() => {
  if (props.trend === null) return null;
  return props.invertTrend ? props.trend < 0 : props.trend > 0;
});

const hasDelta = computed(() => {
  return props.previousValue !== null && props.showDelta;
});

const previousIsZero = computed(() => {
  return props.previousValue === 0;
});

const deltaPercent = computed(() => {
  if (!hasDelta.value || previousIsZero.value) return 0;
  const current = Number(props.value) || 0;
  return ((current - props.previousValue) / props.previousValue) * 100;
});

const deltaDirection = computed(() => {
  if (!hasDelta.value) return null;
  if (previousIsZero.value) return 'new';
  if (deltaPercent.value > 0) return 'up';
  if (deltaPercent.value < 0) return 'down';
  return 'same';
});

const deltaLabel = computed(() => {
  if (deltaDirection.value === 'new') return t('CRM_REPORTS.KPI_CARD.NEW');
  if (deltaDirection.value === 'same') return '0%';
  return `${Math.abs(deltaPercent.value).toFixed(1)}%`;
});

const deltaColorClass = computed(() => {
  switch (deltaDirection.value) {
    case 'up':
      return 'text-green-600 dark:text-green-400';
    case 'down':
      return 'text-red-500 dark:text-red-400';
    case 'new':
      return 'text-blue-500 dark:text-blue-400';
    case 'same':
    default:
      return 'text-n-slate-9';
  }
});

// Setas de texto (padrao BI): ↑ / ↓ + percentual + "vs periodo anterior".
const deltaArrow = computed(() => {
  switch (deltaDirection.value) {
    case 'up':
      return '↑';
    case 'down':
      return '↓';
    default:
      return '';
  }
});
</script>

<template>
  <div
    class="bg-n-solid-2 rounded-xl p-4 flex flex-col gap-1.5 relative overflow-hidden shadow-sm outline outline-1 outline-n-container"
  >
    <span
      class="absolute top-0 left-0 right-0 h-[3px]"
      :style="{ backgroundColor: categoryColor }"
    />
    <div class="flex items-center gap-1.5">
      <span v-if="icon" :class="icon" class="size-3.5 text-n-slate-9" />
      <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">
        {{ label }}
      </span>
      <span
        v-if="tooltip"
        class="i-lucide-info size-3 text-n-slate-9 cursor-help flex-shrink-0"
        @mouseenter="showTooltip = true"
        @mouseleave="showTooltip = false"
      />
      <div
        v-if="showTooltip && tooltip"
        class="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-[12px] text-white bg-slate-900 dark:bg-slate-800 rounded-lg shadow-lg min-w-[280px] max-w-[360px] whitespace-normal leading-relaxed pointer-events-none"
      >
        {{ tooltip }}
        <div
          class="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-900 dark:border-t-slate-800"
        />
      </div>
    </div>
    <span v-if="subtitle" class="text-[10px] text-n-slate-9">
      {{ subtitle }}
    </span>
    <div
      v-if="loading"
      class="w-16 h-7 rounded-md bg-n-slate-3 animate-pulse"
    />
    <div
      v-else
      class="text-[25px] leading-tight tracking-tight tabular-nums font-bold font-headline text-n-slate-12"
    >
      {{ formattedValue }}
    </div>
    <div
      v-if="!loading && hasDelta"
      class="flex items-center gap-1 text-xs font-medium"
      :class="deltaColorClass"
    >
      <span>{{ deltaArrow }} {{ deltaLabel }}</span>
      <span class="text-n-slate-9 font-normal">
        {{ $t('REPORT.VS_PREVIOUS_PERIOD') }}
      </span>
    </div>
    <div
      v-if="!loading && trend !== null"
      class="flex items-center gap-1 text-xs"
    >
      <span
        class="font-medium"
        :class="
          trendIsPositive
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-500 dark:text-red-400'
        "
      >
        {{ trend > 0 ? '↑' : '↓' }} {{ Math.abs(trend).toFixed(1) }}%
      </span>
      <span class="text-n-slate-9">{{ $t('REPORT.VS_PREVIOUS_PERIOD') }}</span>
    </div>
  </div>
</template>
